import {merge} from "lodash";
import {addLine, addQuadrant, addText} from "./utilities";

/**
 * Extract categories from chart series data.
 *
 * @param {Array} data
 * @returns {Array}
 */
export function extractCategories(data) {
    if (!data || data.length === 0) {
        return [];
    }

    if (data[0].x !== undefined) {
        return data[0].x.map((item) => item);
    }

    return data[0].categories || [];
}

/**
 * Build chart options by merging base options with custom overrides.
 *
 * @param {Object} params
 * @param {Array} params.series - Chart series data.
 * @param {Object} [params.customOptions] - Custom Highcharts options to merge in.
 * @param {Object} [params.extras] - Extra features (labels, lines, affinity index, etc.).
 * @param {HTMLElement} params.container - The DOM element to render the chart into.
 * @param {string} [params.title] - Export title for the chart.
 * @returns {Object}
 */
export function buildChartOptions({series, customOptions = {}, extras = {}, container, title = ''}) {
    const categories = extractCategories(series);

    const options = {
        chart: {
            events: {
                load: function () {
                    // The copy drawn for an image export is already the size
                    // asked for. Resizing it would fit it back to the chart on
                    // the page, and grow that chart's container every time.
                    if (this.options.chart.forExport) {
                        return;
                    }

                    adjustLegendHeight(this, container);
                },
                render: function () {
                    renderExtras(this, extras);

                    const chart = this;

                    if (extras.dynamicComparisonColumnWidth) {
                        applyDynamicComparisonColumnWidths(chart, extras.dynamicComparisonColumnWidth);
                    }

                    setTimeout(() => automaticDataLabelCollisionDetection(chart), 0);
                }
            }
        },
        exporting: {
            chartOptions: {
                title: {
                    text: title,
                }
            },
        },
        series: series,
    };

    if (categories.length > 0) {
        options.xAxis = {categories};
    }

    const combinedOptions = merge(options, customOptions);

    if (extras.showAffinityIndex) {
        applyAffinityIndexTooltip(combinedOptions);
    }

    return combinedOptions;
}

/**
 * Create a new Highcharts chart or update an existing one.
 *
 * @param {Object} params
 * @param {Highcharts} params.Highcharts - The Highcharts library instance.
 * @param {HTMLElement} params.container - The DOM element to render the chart into.
 * @param {Object} params.options - The combined Highcharts options.
 * @param {Object|null} [params.existingChart] - An existing chart instance to update instead of re-creating.
 * @param {string} [params.chartConstructor] - The Highcharts constructor to draw with: chart, stockChart or mapChart.
 * @returns {Object} The chart instance.
 */
export function renderChart({Highcharts, container, options, existingChart = null, chartConstructor = 'chart'}) {
    if (existingChart) {
        existingChart.update(options);

        return existingChart;
    }

    const chart = Highcharts[chartConstructor](container, options);
    chart.render();

    return chart;
}

/**
 * Fetch the GeoJSON a map chart is drawn on.
 *
 * @param {string} url
 * @returns {Promise<Object>}
 */
export function fetchMap(url) {
    return fetch(url)
        .then(response => response.json())
        .catch(error => {
            console.error(error);

            return {};
        });
}

/**
 * Adjust chart height to account for legend overflow.
 *
 * @param {Object} chart - The Highcharts chart instance (this context from event).
 * @param {HTMLElement} container - The chart container element.
 */
export function adjustLegendHeight(chart, container) {
    // A floating legend is drawn over the plot, so it takes no room.
    if (chart.legend.options.floating) {
        return;
    }

    const legendHeight = chart.legend.legendHeight - chart.legend.padding;

    if (legendHeight) {
        chart.setSize(null, chart.chartHeight + legendHeight);
        container.style.height = (container.offsetHeight + legendHeight) + 'px';
    }
}

/**
 * Render extra areas, lines and labels on the chart, in that order so the
 * labels sit on top. Positions are in axis values. A position can also be
 * 'min' or 'max' to follow the axis extremes, or {from: 'max', offset: -15}
 * to sit a distance from them.
 *
 * @param {Object} chart - The Highcharts chart instance.
 * @param {Object} extras
 */
export function renderExtras(chart, extras) {
    const x = (value) => resolveAxisValue(chart.xAxis[0], value);
    const y = (value) => resolveAxisValue(chart.yAxis[0], value);

    (extras.areas || []).forEach((area) => {
        addQuadrant(chart, area.key, x(area.x1), y(area.y1), x(area.x2), y(area.y2), area.attr || {});
    });

    (extras.lines || []).forEach((line) => {
        addLine(chart, line.key, x(line.x1), y(line.y1), x(line.x2), y(line.y2), line.style || {});
    });

    (extras.labels || []).forEach((label) => {
        addText(chart, label.key, label.label, x(label.x), y(label.y), label.css || {}, label.attr || {});
    });
}

/**
 * @param {Object} axis - A Highcharts axis.
 * @param {number|string|Object} value
 * @returns {number}
 */
export function resolveAxisValue(axis, value) {
    if (typeof value === 'string') {
        return axis[value];
    }

    if (value && typeof value === 'object') {
        return axis[value.from] + (value.offset || 0);
    }

    return value;
}

/**
 * Apply the affinity index tooltip formatter to chart options.
 *
 * @param {Object} options - Highcharts options object (mutated in place).
 */
export function applyAffinityIndexTooltip(options) {
    options.tooltip = options.tooltip || {};
    options.tooltip.shared = true;

    options.tooltip.formatter = function () {
        let tooltip = '';
        tooltip += `<span class="chart-tooltip-series-label">${this.key}</span>`;

        this.points.forEach((point, key) => {
            tooltip += `<span class="chart-tooltip-point ${key !== 0 ? 'has-small-spacing' : ''}"><span class="chart-tooltip-point-marker" style="color:${point.color};">•</span> <span class="chart-tooltip-point-name">${point.series.name}</span>: <span class="chart-tooltip-point-value">${point.y.toFixed(2)} %</span></span>`;

            if (key !== 0) {
                const affinityIndex = this.points[key].point.y / this.points[0].point.y * 100;
                tooltip += `<span class="chart-tooltip-point"><span style="width: 20px; display:inline-block;"></span><span class="chart-tooltip-point-name">Affinity Index</span>: <span class="chart-tooltip-point-value">${affinityIndex.toFixed(2)}</span></span>`;
            }
        });

        return tooltip;
    };
}

/**
 * Size paired primary/comparison column series so the comparison columns are
 * a small fixed amount wider than the primary on each side, while the
 * primary itself adapts to the available category slot.
 *
 * The PHP side spreads N entities across 80% of the category width using
 * pointPlacement, so the spacing between entity centres is
 * (slotWidth * 0.8) / N for N >= 2. Bars must fit inside that inter-entity
 * window without overlapping their neighbours.
 *
 * @param {Object} chart - The Highcharts chart instance.
 * @param {Object} config
 * @param {number} config.extraPx - Total extra width given to comparison
 *                                  series, half on each side.
 * @param {number} config.fillRatio - Fraction of the inter-entity slot the
 *                                    primary column should occupy (0..1).
 * @param {number} config.minWidth - Floor for the computed primary width.
 */
export function applyDynamicComparisonColumnWidths(chart, config) {
    if (chart._adjustingComparisonWidths) {
        return;
    }

    const xAxis = chart.xAxis && chart.xAxis[0];
    if (!xAxis || !xAxis.len) {
        return;
    }

    const categoryCount = (xAxis.categories && xAxis.categories.length) || 1;
    const primarySeries = chart.series.filter(s => s.options.id && s.options.id.startsWith('primary-'));
    const entityCount = primarySeries.length || 1;

    // Mirrors the `* 0.8` factor in PillarsComparison::transformDataToChartSeries().
    const PLACEMENT_SPAN = 0.8;
    const slotWidth = xAxis.len / categoryCount;
    const interEntitySlot = entityCount === 1
        ? slotWidth * PLACEMENT_SPAN
        : (slotWidth * PLACEMENT_SPAN) / entityCount;

    const minPrimary = config.minWidth || 8;
    const maxPrimary = config.maxWidth || 60;
    const fillRatio = config.fillRatio || 0.5;
    const extraPx = config.extraPx || 0;

    // Determine the primary first based on the available slot, then derive
    // the comparison from it. In narrow charts, prefer to shrink the
    // primary→comparison delta over crushing the primary against its floor.
    const primaryWidth = Math.min(maxPrimary, Math.max(minPrimary, interEntitySlot * fillRatio));

    const cap = Math.max(minPrimary + 4, interEntitySlot - 4);
    const desiredComparison = primaryWidth + extraPx;
    const comparisonWidth = Math.min(desiredComparison, Math.max(primaryWidth + 4, cap));

    let needsRedraw = false;

    chart._adjustingComparisonWidths = true;

    try {
        chart.series.forEach(series => {
            const id = series.options.id;
            if (!id) {
                return;
            }

            let target = null;
            if (id.startsWith('primary-')) {
                target = primaryWidth;
            } else if (id.startsWith('comparison-')) {
                target = comparisonWidth;
            }

            if (target === null) {
                return;
            }

            // Tolerate sub-pixel jitter so we don't trigger a redraw loop.
            if (Math.abs((series.options.pointWidth || 0) - target) < 0.5) {
                return;
            }

            series.update({pointWidth: target}, false);
            needsRedraw = true;
        });

        if (needsRedraw) {
            chart.redraw(false);
        }
    } finally {
        chart._adjustingComparisonWidths = false;
    }
}

function automaticDataLabelCollisionDetection(chart) {
    // Re-entrancy guard: prevent render → move → render loops
    if (chart._labelCollisionRunning) {
        return;
    }

    chart._labelCollisionRunning = true;

    try {
        resolveDataLabelCollisions(chart);
    } finally {
        chart._labelCollisionRunning = false;
    }
}

function resolveDataLabelCollisions(chart) {
    const items = [];
    const containerRect = chart.container.getBoundingClientRect();
    const plotBounds = {
        left: containerRect.x + chart.plotLeft,
        top: containerRect.y + chart.plotTop,
        right: containerRect.x + chart.plotLeft + chart.plotWidth,
        bottom: containerRect.y + chart.plotTop + chart.plotHeight,
    };

    chart.series.forEach(series => {
        if (!series.visible) {
            return;
        }

        series.points.forEach(point => {
            if (!point.dataLabel || !point.dataLabel.element) {
                return;
            }

            const label = point.dataLabel;
            const rect = label.element.getBoundingClientRect();

            items.push({
                label,
                baseRect: rect,
                width: rect.width,
                height: rect.height,
                origTranslateX: label.translateX || 0,
                origTranslateY: label.translateY || 0,
                dx: 0,
                dy: 0,
            });
        });
    });

    if (items.length < 2) {
        return;
    }

    // Candidate offsets relative to the default Highcharts label position.
    // Moves the label to a different side of its data point.
    const getCandidateOffsets = (item) => {
        const w = item.width;
        const h = item.height;
        const g = 4;

        return [
            { dx: 0, dy: 0 },                 // Keep default
            { dx: 0, dy: h + g },              // Below
            { dx: 0, dy: -(h + g) },           // Above
            { dx: w + g, dy: 0 },              // Right
            { dx: -(w + g), dy: 0 },           // Left
            { dx: w / 2, dy: h + g },          // Below-right
            { dx: -(w / 2), dy: h + g },       // Below-left
            { dx: w / 2, dy: -(h + g) },       // Above-right
            { dx: -(w / 2), dy: -(h + g) },    // Above-left
        ];
    };

    // Sort: process top-right labels first (typically more isolated)
    items.sort((a, b) => a.baseRect.y - b.baseRect.y || b.baseRect.x - a.baseRect.x);

    // Greedy candidate placement — pick the position with least overlap,
    // preferring positions closer to the default.
    const placedRects = [];

    for (const item of items) {
        const candidates = getCandidateOffsets(item);
        let bestDx = 0;
        let bestDy = 0;
        let bestScore = Infinity;

        for (const { dx, dy } of candidates) {
            const cx = item.baseRect.x + dx;
            const cy = item.baseRect.y + dy;

            // Skip candidates that fall outside the plot area
            if (cx + item.width < plotBounds.left ||
                cx > plotBounds.right ||
                cy + item.height < plotBounds.top ||
                cy > plotBounds.bottom) {
                continue;
            }

            let totalOverlap = 0;

            for (const p of placedRects) {
                const ox = Math.min(cx + item.width, p.x + p.width) - Math.max(cx, p.x);
                const oy = Math.min(cy + item.height, p.y + p.height) - Math.max(cy, p.y);

                if (ox > 0 && oy > 0) {
                    totalOverlap += ox * oy;
                }
            }

            // Score: overlap area is dominant, with a small distance tiebreaker
            // so that zero-overlap candidates closer to default are preferred.
            const distance = Math.abs(dx) + Math.abs(dy);
            const score = totalOverlap * 1000 + distance;

            if (score < bestScore) {
                bestScore = score;
                bestDx = dx;
                bestDy = dy;
            }

            // Perfect position at default — stop searching
            if (totalOverlap === 0 && dx === 0 && dy === 0) {
                break;
            }
        }

        item.dx = bestDx;
        item.dy = bestDy;

        placedRects.push({
            x: item.baseRect.x + bestDx,
            y: item.baseRect.y + bestDy,
            width: item.width,
            height: item.height,
        });
    }

    // Apply adjustments via translateX/translateY
    for (const item of items) {
        if (Math.abs(item.dx) < 0.5 && Math.abs(item.dy) < 0.5) {
            continue;
        }

        item.label.attr({
            translateX: item.origTranslateX + item.dx,
            translateY: item.origTranslateY + item.dy,
        });
    }
}
